import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import TextField from "@material-ui/core/TextField";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import {
  MenuItem,
  Typography,
  Button,
  Grid,
  FormHelperText,
  CircularProgress,
  Divider,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import AlertTitle from "@material-ui/lab/AlertTitle";

import { useForm, Controller } from "react-hook-form";
import JSZip from "jszip";
import TreeItem from "@material-ui/lab/TreeItem";
import TreeView from "@material-ui/lab/TreeView";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import Modal from "../../cm_shared/cm_global_modal";
import { LoadingContext } from "../../../../context/spinnerContext";

const requiredFiles = [
  {
    format: ".tr",
    title: "Test Run",
  },
  {
    format: ".ts",
    title: "Test Run",
  },
];

const CMUpload = (props) => {  
  const { setIsLoading, isLoading:loading } = useContext(LoadingContext);
  const [fileSystemList, setFileSystemList] = useState([]);
  const [simulatorList, setSimulatorList] = useState([]);
  const [modelList, setModelList] = useState([]);
  const [zipFileContents, setZipFileContents] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [errorMessages, setErrorMessages] = useState([]);
  const [alertMessages, setAlertMessages] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  // const [loading, setIsLoading] = useState(false);
  const isOpenLoop = props.isOpenLoop;

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    control,
  } = useForm();

  useEffect(() => {
    if(isOpenLoop) return;
    const fetchSimulatorList = async () => {
      try {
        const response = await axios.post(
          "/cm/close_loop/carmaker_simulator_record_v2"
        );
        setSimulatorList(response.data);
        if (response.data.length > 0) {
          setValue("simulator", response.data[0]._id);
        }
      } catch (error) {
        console.log("Failed to fetch simulators");
      }
    };

    const fetchModelList = async () => {
      try {
        const response = await axios.post(
          "/cm/close_loop/fetch-carmaker_model_record_V2"
        );
        setModelList(response.data);
        if (response.data.length > 0) {
          setValue("model", response.data.filter(item => item.value === 'Generic')[0]._id);
        }
      } catch (error) {
        console.log("Failed to fetch models");
      }
    };

    const fetchFileSystemList = async () => {
      try {
        const fileSystemOptions = [
          { _id: "s3-filesystem", value: "Cloud-FileSystem" },
          { _id: "local-filesystem", value: "Local-FileSystem" },
        ];
        setFileSystemList(fileSystemOptions);
        if (fileSystemOptions.length > 0) {
          setValue("fileSystem", fileSystemOptions[0]._id);
        }
      } catch (error) {
        console.log("Failed to fetch file system list");
      }
    };
    
    fetchFileSystemList();
    fetchSimulatorList();
    fetchModelList();
  }, [setValue]);
  
  const formFields = [
    {
      id: "files",
      type: "file",
      label: "Zip File",
      validation: { required: "Zip file is required", validate:(value) => {
        console.log(value);
        if(value && value.length && value[0].size > 1000000000) {
          return "File size should not exceed 1gb"
        }
      }},
    },
    ...(!isOpenLoop ? [{
      id: "fileSystem",
      type: "select",
      options: fileSystemList,
      // defaultValue:
      //   fileSystemList.length > 0 ? fileSystemList[0]._id : "s3-filesystem",
      label: "File System",
      validation: { required: "File System is needed" },
    },
    {
      id: "model",
      type: "select",
      // options: modelList.length > 0 ? modelList : [],
      // defaultValue: modelList.length > 0 ? modelList[0]._id : "",
      options: modelList,
      label: "Model",
      validation: { required: "Model is needed" },
    },
    {
      id: "simulator",
      type: "select",
      // options: simulatorList.length > 0 ? simulatorList : [],
      // defaultValue: simulatorList.length > 0 ? simulatorList[0]._id : "",
      options: simulatorList,
      label: "Simulator",
      validation: { required: "Simulator is needed" },
    },
  ] : []),
  {
    id: "userDescription",
    type: "text",
    label: "User Description",
  }
]

console.log("formField Closeloop", formFields);

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    setSelectedFile(file);
    if (file) {
      const zip = new JSZip();
      const contents = await zip.loadAsync(file);
      const files = await processZipContents(contents);
      setZipFileContents(files);
    }
  };

  const processZipContents = async (zip, parentPath = "") => {
    const files = [];
    const fileKeys = Object.keys(zip.files);
    for (const fileKey of fileKeys) {
      const file = zip.files[fileKey];
      const filePath = `${parentPath}${fileKey}`;

      if (file.dir) {
        const containsSubDir = fileKeys.some((nestedFileKey) => {
          const nestedFile = zip.files[nestedFileKey];
          return (
            nestedFile.dir &&
            nestedFileKey.startsWith(fileKey) &&
            nestedFileKey !== fileKey
          );
        });

        files.push({
          path: filePath,
          isDir: true,
          isLastLevel: !containsSubDir,
        });
      } else {
        files.push({ path: filePath, isDir: false });
      }

      if (fileKey.endsWith(".zip")) {
        const nestedZipBuffer = await file.async("nodebuffer");
        const nestedZip = await JSZip.loadAsync(nestedZipBuffer);
        const nestedFiles = await processZipContents(nestedZip, `${filePath}/`);
        files.push(...nestedFiles);
      }
    }
    return files;
  };

  const checkRequiredFiles = (fileList) => {
    return requiredFiles.map((reqFile) => {
      const matchingFile = fileList.find((file) =>
        file.path.endsWith(reqFile.format)
      );
      return {
        ...reqFile,
        present: Boolean(matchingFile),
        fileName: matchingFile ? matchingFile.path : "",
      };
    });
  };

  const buildFileTree = (files) => {
    const fileStructure = {};

    files.forEach((file) => {
      const parts = file.path.split("/");
      let currentLevel = fileStructure;

      parts.forEach((part, index) => {
        if (!currentLevel[part]) {
          currentLevel[part] = {
            children: {},
            path: parts.slice(0, index + 1).join("/"),
            missingFiles: [],
            isLastLevel: file.isLastLevel || false,
          };
        }
        if (index === parts.length - 1) {
          currentLevel[part].isFile = !file.isDir;
        }
        currentLevel = currentLevel[part].children;
      });
    });

    const markFolders = (structure, parentHasRequiredFiles = false) => {
      Object.keys(structure).forEach((key) => {
        const node = structure[key];

        const childFiles = Object.values(node.children)
          .filter((child) => child.isFile)
          .map((child) => ({ path: child.path, isDir: child.isDir }));

        if (node.isLastLevel) {
          const requiredFileStatuses = checkRequiredFiles(childFiles);
          node.hasRequiredFiles = requiredFileStatuses.every(
            (file) => file.present
          );
          node.missingFiles = requiredFileStatuses
            .filter((file) => !file.present)
            .map((file) => file.format);
        }

        const childKeys = Object.keys(node.children);
        const areAllChildrenFiles = childKeys.every(
          (childKey) => node.children[childKey].isFile
        );

        if (areAllChildrenFiles) {
          node.isLastLevel = true;
        }

        if (!node.isLastLevel) {
          markFolders(node.children);
        }

        if (parentHasRequiredFiles) {
          node.hasRequiredFiles = true;
          node.missingFiles = [];
        }
      });

      return Object.values(structure).every((node) => node.hasRequiredFiles);
    };

    markFolders(fileStructure);

    const createTreeItems = (structure) => {
      return Object.keys(structure).map((key) => {
        const node = structure[key];
        const label = (
          <div
            style={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <Typography
              style={{
                color: "inherit",
                fontSize: "18px",
              }}
            >
              <span>{key}</span>
            </Typography>
          </div>
        );

        if (Object.keys(node.children).length > 0) {
          return (
            <TreeItem key={node.path} nodeId={node.path} label={label}>
              {createTreeItems(node.children)}
            </TreeItem>
          );
        } else {
          return <TreeItem key={node.path} nodeId={node.path} label={label} />;
        }
      });
    };

    return createTreeItems(fileStructure);
  };

  const validateFiles = () => {
    const errors = [];
    return errors;
  };

  const onSubmit = async (data) => {
    if(isOpenLoop) return;
    const validationErrors = validateFiles();

    if (validationErrors.length > 0) {
      setErrorMessages(validationErrors);
      return;
    }

    setIsLoading(true);
    try {
      const filesToProcess = [];
      zipFileContents.forEach((zipFileContent) => {
        if (zipFileContent.isLastLevel === true) {
          filesToProcess.push(zipFileContent.path);
        }
      });

      const formData = new FormData();
      if(!isOpenLoop){
        formData.append("file_system", data.fileSystem);
        formData.append("model", data.model);
        formData.append("simulator", data.simulator);
      }
      formData.append("file", selectedFile);
      formData.append("description", data.userDescription);
      console.log('form data', formData);
      let apiUrl = `/cm/close_loop/carmaker_scenario_file_v2/upload`;

      const response = await axios.post(apiUrl, formData);
      setModalContent(
        <Alert severity="success" style={{ marginTop: "20px" }}>
          <AlertTitle>Upload Status</AlertTitle>
          <ul style={{ padding: "2px" }}>
            {Object.entries(response.data).map((message, index) => (
              <li key={index} style={{ listStyleType: "none" }}>
                {message[0]} {message[1]}
              </li>
            ))}
          </ul>
          <Button onClick={props.onSuccess}>Close</Button>
        </Alert>
      );
      setIsOpen(true);

      // form.reset();
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fileTree = buildFileTree(zipFileContents);

  return (
    <div style={{ width: "100%" }}>
      <Modal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        content={modalContent}
        size={"lg"}
      />
      <Grid container>
        <Grid
          item
          xs={zipFileContents.length > 0 ? 6 : 12}
          md={zipFileContents.length > 0 ? 6 : 12}
          style={{
            height: "100%",
            justifyContent:
              zipFileContents.length > 0 ? "flex-start" : "center",
          }}
        >
          <Typography variant="h5" style={{ marginBottom: "10px" }}>
            Upload Scenario File(s)
          </Typography>
          <Divider />
          <div style={{ marginTop: "20px" }}>
            <form onSubmit={handleSubmit(onSubmit)}>
              { (isOpenLoop ? true :fileSystemList.length > 0 ) &&
                formFields.map((formField) => (
                  <div
                    key={formField.id}
                    style={{ display: "flex", padding: "5px" }}
                  >
                    <div style={{ width: "30%" }}>
                      <Typography variant="h6">{formField.label}:</Typography>
                    </div>
                    <div style={{ width: "70%" }}>
                      {formField.type === "file" ? (
                        <div>
                          <input
                            type="file"
                            id={formField.id}
                            {...register(formField.id, formField.validation)}
                            onChange={handleFileChange}
                            accept="application/zip"
                            disabled={loading} // Disable input during loading
                          />
                          {errors[formField.id] && (
                            <FormHelperText error>
                              {errors[formField.id].message}
                            </FormHelperText>
                          )}
                        </div>
                      ) : formField.type === "select" ? (
                        <FormControl
                          variant="outlined"
                          fullWidth
                          error={!!errors[formField.id]}
                        >
                          <Controller
                            name={formField.id}
                            control={control}
                            defaultValue=""
                            render={({ field }) => (
                              <Select
                                {...field}
                                onChange={(e) => {
                                  field.onChange(e);
                                  setValue(formField.id, e.target.value);
                                }}
                                value={field.value || ""}
                                style={{
                                  width:
                                    zipFileContents.length > 0 ? "100%" : "60%",
                                }}
                                disabled={loading || formField.label === 'Model'}
                              >
                                {formField.options.map((option) => (
                                  <MenuItem key={option._id} value={option._id}>
                                    {option.value}
                                  </MenuItem>
                                ))}
                              </Select>
                            )}
                          />
                          {errors[formField.id] && (
                            <FormHelperText>
                              {errors[formField.id].message}
                            </FormHelperText>
                          )}
                        </FormControl>
                      ) : (
                        <FormControl
                          variant="outlined"
                          fullWidth
                          error={!!errors[formField.id]}
                        >
                          <TextField
                            style={{
                              width:
                                zipFileContents.length > 0 ? "100%" : "60%",
                            }}
                            type={formField.type}
                            variant="outlined"
                            {...register(formField.id, formField.validation)}
                            disabled={loading} // Disable input during loading
                          />
                          {errors[formField.id] && (
                            <FormHelperText>
                              {errors[formField.id].message}
                            </FormHelperText>
                          )}
                        </FormControl>
                      )}
                    </div>
                  </div>
                ))}
              <div style={{ display: "flex", justifyContent: "center" }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  style={{ marginTop: "2%", width: "200px" }}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : "Upload"}
                </Button>

                <Button
                  onClick={() => props.closeModal(false)}
                  variant="outlined"
                  style={{
                    marginTop: "2%",
                    width: "200px",
                    marginLeft: "10px",
                  }}
                  disabled={loading? true:false}
                >
                  {loading ? <CircularProgress size={24} /> : "Cancel"}
                </Button>
              </div>
            </form>
          </div>
          {errorMessages.length > 0 && (
            <div style={{ marginTop: "20px", color: "red" }}>
              {errorMessages.map((error, index) => (
                <Typography key={index}>{error}</Typography>
              ))}
            </div>
          )}
        </Grid>
        {!alertMessages && zipFileContents.length ? (
          <Grid
            item
            xs={zipFileContents.length > 0 ? 6 : 0}
            md={zipFileContents.length > 0 ? 6 : 0}
            style={{ height: "300px", overflowY: "auto", marginTop: "50px" }}
          >
            <TreeView
              defaultCollapseIcon={<ExpandMoreIcon />}
              defaultExpandIcon={<ChevronRightIcon />}
            >
              {fileTree}
            </TreeView>
          </Grid>
        ) : null}
        {alertMessages.length > 0 && (
          <Alert severity="success" style={{ marginTop: "20px" }}>
            <AlertTitle>Upload Status</AlertTitle>
            <ul style={{ padding: "2px" }}>
              {alertMessages.map((message, index) => (
                <li key={index} style={{ listStyleType: "none" }}>
                  {message[0]} {message[1]}
                </li>
              ))}
            </ul>
          </Alert>
        )}
      </Grid>
    </div>
  );
};

export default CMUpload;
